import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { Mode as IonicMode } from '@ionic/core';
import { createState, select, Store, withProps } from '@ngneat/elf';
import { localStorageStrategy, persistState } from '@ngneat/elf-persist-state';

export interface SettingsProperties {
  mode: Mode;
  theme: Theme;
  showPlannerOnBoarding: boolean;
  showWelcomeOnboarding: boolean;
}

export enum Mode {
  System = 'system',
  Android = 'android',
  Ios = 'ios',
}

export enum Theme {
  System = 'system',
  Light = 'light',
  Dark = 'dark',
}

const { state, config } = createState(
  withProps<SettingsProperties>({
    mode: Mode.System,
    theme: Theme.Light,
    showPlannerOnBoarding: true,
    showWelcomeOnboarding: true,
  }),
);
const store = new Store({ name: 'settings', state, config });

const persistStateInstance = persistState(store, {
  key: environment.localStorageKeyPrefix + 'settings',
  /**
   * Must be `localStorageStrategy` so the app bootstrap can synchronously
   * read `mode` before Angular is ready (avoids a theme flash on startup).
   */
  storage: localStorageStrategy,
});

export const getModeForIonicModule = (): IonicMode | undefined => {
  const { mode } = store.getValue();
  switch (mode) {
    case Mode.Android: {
      return 'md';
    }
    case Mode.Ios: {
      return 'ios';
    }
    default: {
      return;
    }
  }
};

@Injectable({ providedIn: 'root' })
export class SettingsRepository {
  public initialized$ = persistStateInstance.initialized$;
  public theme$ = store.pipe(select((state) => state.theme));
  public mode$ = store.pipe(select((state) => state.mode));
  public showPlannerOnBoarding$ = store.pipe(
    select((state) => state.showPlannerOnBoarding),
  );
  public showWelcomeOnboarding$ = store.pipe(
    select((state) => state.showWelcomeOnboarding),
  );

  public setMode(mode: SettingsProperties['mode']): void {
    store.update((state) => ({
      ...state,
      mode,
    }));
  }

  public getMode(): SettingsProperties['mode'] {
    const { mode } = store.getValue();
    return mode;
  }

  public setTheme(theme: SettingsProperties['theme']): void {
    store.update((state) => ({
      ...state,
      theme,
    }));
  }

  public setShowPlannerOnBoarding(show: boolean): void {
    store.update((state) => ({
      ...state,
      showPlannerOnBoarding: show,
    }));
  }

  public setShowWelcomeOnboarding(show: boolean): void {
    store.update((state) => ({
      ...state,
      showWelcomeOnboarding: show,
    }));
  }

  public getTheme(): SettingsProperties['theme'] {
    const { theme } = store.getValue();
    return theme;
  }

  public getShowPlannerOnBoarding(): boolean {
    const { showPlannerOnBoarding } = store.getValue();
    return showPlannerOnBoarding;
  }

  public getShowWelcomeOnboarding(): boolean {
    const { showWelcomeOnboarding } = store.getValue();
    return showWelcomeOnboarding;
  }

  public clear(): void {
    this.setMode(Mode.System);
    this.setTheme(Theme.System);
  }
}
